<template>
  <div class="form">
    <div class="payment-methods">
      <form id="subscription-form" @submit.prevent="subscribeHandler">
        <div id="payment-methods" v-if="showPaymentMethods">
          <div
            v-for="(paymentMethod, index) in paymentMethods"
            :key="index"
            class="payment-method-list-item"
          >
            <label :for="paymentMethod.id" class="field-input">
              <div class="payment-method-list-item-container">
                <div class="payment-method-list-item-input">
                  <input
                    type="radio"
                    :value="paymentMethod.id"
                    :id="paymentMethod.id"
                    v-model="selectedPaymentMethod"
                  />
                </div>
                <div class="payment-method-list-item-card-icon">
                  <i class="fa-2x" :class="getCardIcon(paymentMethod.card)" />
                </div>

                <div class="payment-method-list-item-card-details">
                  <div class="payment-method-list-item-card-details-number">
                    {{ cardBrand(paymentMethod.card) }}
                    {{ last4Formatted(paymentMethod.card) }}
                  </div>
                  <div class="payment-method-list-item-card-details-expiry">
                    {{ expiryFormatted(paymentMethod.card) }}
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>
        <AddNewPaymentMethod
          :stripe-public-key="stripePublicKey"
          @payment-method-created="refreshPaymentMethods"
          @show-form="showAddPaymentMethodHandler"
          :payment-methods="paymentMethods"
          @emit-global-loading="$emit('emit-global-loading', $event)"
        />

        <button
          class="button expand primary"
          type="submit"
          v-if="paymentMethods.length > 0 && showPaymentMethods"
        >
          <span>Subscribe</span>
          <span v-if="itemPriceFormatted">{{ itemPriceFormatted }}</span>
        </button>
        <PaymentError :error="displayError" />
      </form>
    </div>
  </div>
</template>
<script>
import {
  instantiateStripe,
  createPaymentMethod,
  getPaymentMethods,
  subscribe,
  confirmSecret,
  confirmSetupCardSecret,
} from '../stripe-elements';
import PaymentError from '../PaymentError.vue';
import PaymentsMixin from '../PaymentsMixin.vue';
import NameOnCard from '../NameOnCard.vue';
import AddNewPaymentMethod from './AddNewPaymentMethod.vue';

export default {
  name: 'SCACreateSubscriptionCardForm',
  mixins: [PaymentsMixin],
  components: {
    NameOnCard,
    PaymentError,
    AddNewPaymentMethod,
  },
  data() {
    return {
      displayError: '',
      disabled: true,
      nameOnCard: '',
      nameError: false,
      paymentMethods: [],
      selectedPaymentMethod: '',
      showPaymentMethods: true,
    };
  },
  props: {
    stripePublicKey: {
      type: String,
      required: true,
    },
    stripeAccountId: {
      type: String,
      required: true,
    },
    orderId: {
      type: String,
      required: false,
    },
    paymentRequestOptions: {
      required: true,
      type: Object,
    },
  },
  computed: {
    itemPriceFormatted() {
      return this.itemAmount > 0
        ? `and Pay ${this.formatMoney(this.itemAmount)}`
        : '';
    },
    subscribeButtonText() {
      return this.parentHasSubscription ? 'Update Subscription' : 'Subscribe';
    },
    itemAmount() {
      return this.paymentRequestOptions.total.amount;
    },
  },
  methods: {
    async refreshPaymentMethods(value) {
      await this.getPaymentMethods();
      this.showPaymentMethods = true;
    },
    subscribeHandler() {
      this.subscribe();
    },

    async subscribe() {
      if (this.selectedPaymentMethod === '') {
        return;
      }
      this.$emit('emit-global-loading', true);
      const paymentMethod = this.selectedPaymentMethod;
      if (paymentMethod) {
        this.mountStripeForConnectPayment();

        const { data, status } = await subscribe(paymentMethod, this.orderId);

        if (status === false) {
          // fail
          this.handleErrorMessages(data);
        } else if (data.status === 'trialing' && !data.pending_setup_intent) {
          // non sca
          const { latest_invoice } = data;
          const paymentIntentId = latest_invoice.payment_intent
            ? latest_invoice.payment_intent.id
            : undefined;
          this.confirmTheSubscriptionAndRedirect(data.id, paymentIntentId);
        } else {
          // sca
          this.strongCustomerAuthentication(data);
        }
      }
    },

    async strongCustomerAuthentication(data) {
      const subscription = data;
      const latestInvoice = subscription.latest_invoice;
      const paymentIntent = latestInvoice.payment_intent;
      const setupIntent = subscription.pending_setup_intent;
      const paymentMethod =
        subscription.customer.invoice_settings.default_payment_method;
      let confirmation = {};
      confirmation = await this.sendSecret(
        paymentIntent,
        setupIntent,
        paymentMethod
      );

      if (confirmation.error) {
        this.handleErrorMessages(confirmation);
      } else {
        const { subscriptionSetupSuccessful, paymentIntentId } =
          this.getSuccessfulSubscriptionStatus(latestInvoice, confirmation);
        if (subscriptionSetupSuccessful === true) {
          this.confirmTheSubscriptionAndRedirect(subscription.id, paymentIntentId);
        } else {
          this.handleErrorMessages();
        }
      }
    },

    async sendSecret(paymentIntent, setupIntent, paymentMethod) {
      let confirmation;
      if (paymentIntent && paymentIntent.status === 'requires_action') {
        confirmation = await confirmSecret(paymentIntent.client_secret, paymentMethod);
      }
      if (setupIntent && (setupIntent.status === 'requires_action' || setupIntent.status === 'requires_payment_method' )) {
        confirmation = await confirmSetupCardSecret(
          setupIntent.client_secret,
          paymentMethod
        );
      }
      return confirmation;
    },
    mountStripeForConnectPayment() {
      const { stripeAccountId, stripePublicKey } = this;

      instantiateStripe(Stripe, {
        stripePublicKey,
        stripeAccountId,
      });
    },

    expiryFormatted(card) {
      return `Expires ${card.exp_month}/${card.exp_year}`;
    },
    getCardIcon(card) {
      let iconString = 'fab';
      if (
        card.brand.toLowerCase() == 'visa' ||
        card.brand.toLowerCase() == 'mastercard'
      ) {
        return `${iconString} fa-cc-${card.brand.toLowerCase()} color-${card.brand.toLowerCase()}`;
      } else if (
        card.brand.toLowerCase() == 'amex' ||
        card.brand.toLowerCase() == 'american express'
      ) {
        return `${iconString} fa-cc-amex color-${card.brand.toLowerCase()}`;
      } else {
        return 'fal fa-credit-card color-credit-card';
      }
    },
    cardBrand(card) {
      const brand = card.brand[0].toUpperCase() + card.brand.substring(1);
      return `${brand}`;
    },
    last4Formatted(card) {
      return `•••• ${card.last4}`;
    },
    showAddPaymentMethodHandler(value) {
      this.showPaymentMethods = !value;
    },
    async getPaymentMethods() {
      this.$emit('emit-global-loading', true);

      const { data } = await getPaymentMethods();
      this.paymentMethods = data;
      this.selectedPaymentMethod = data[0] ? data[0].id : '';

      this.$emit('emit-global-loading', false);
    },
  },

  async mounted() {
    await this.getPaymentMethods();
  },
};
</script>

<style lang="scss" scoped>
.color-visa {
  color: #0157a2;
}
.color-mastercard {
  color: #0a3a82;
}
.color-amex {
  color: #007bc1;
}
.color-credit-card {
  color: #ed5656;
}

form > * + * {
  margin-top: 20px;
}

input {
  border: 0px solid;
}

.payment-method-list-item {
  display: grid;
  grid-template-rows: 1fr;
  margin-bottom: 16px;
  padding: 8px;
  border-radius: 4px;

  &:hover {
    background-color: #f5f5f5;
  }

  .payment-method-list-item-container {
    display: grid;
    grid-template-columns: 2fr 2fr 4fr;
    align-items: center;
  }

  .payment-method-list-item-input,
  .payment-method-list-item-card-icon {
    text-align: center;
  }

  .payment-method-list-item-card-details {
    .payment-method-list-item-card-details-number {
      font-size: 0.9rem;
      font-weight: 600;
    }

    .payment-method-list-item-card-details-expiry {
      font-size: 0.8rem;
      color: rgb(105, 115, 134);
    }
  }
}
</style>
